import { forwardRef } from 'react';

import { Input } from '@heroui/react';
import type { InputProps } from '@heroui/react';

interface CustomInputProps extends Omit<InputProps, 'label'> {
  label?: string;
  placeholder?: string;
  error?: string;
}

export const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ label, placeholder, error, className, classNames, ...props }, ref) => {
    // Merge custom classNames with defaults - using white glassmorphic style
    const mergedClassNames = {
      input: [
        'bg-transparent',
        '!text-gray-800',
        '!text-base',
        'placeholder:text-gray-400',
        'group-data-[has-value=true]:!text-gray-800',
        'data-[filled=true]:!text-gray-800',
        'data-[focus=true]:!text-gray-800',
        'autofill:shadow-[inset_0_0_0px_1000px_rgb(255,255,255)]',
        'autofill:[-webkit-text-fill-color:#1f2937!important]',
        'autofill:!text-base',
        'px-3',
        'py-2',
        ...(classNames?.input || []),
      ],
      inputWrapper: [
        'bg-white/80',
        'backdrop-blur-sm',
        'border-gray-200',
        'hover:border-gray-300',
        'focus-within:!border-violet-400',
        'group-data-[focus=true]:border-violet-400',
        '!cursor-text',
        '!rounded-xl',
        '!p-0',
        '!m-0',
        'shadow-sm',
        'hover:shadow-md',
        'transition-all',
        'duration-200',
        '[&>input]:!text-gray-800',
        '[&_input]:!text-gray-800',
        ...(classNames?.inputWrapper || []),
      ],
      base: ['!p-0', '!m-0', ...(classNames?.base || [])],
      mainWrapper: ['!p-0', '!m-0', ...(classNames?.mainWrapper || [])],
    };

    return (
      <div>
        {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
        <Input
          ref={ref}
          placeholder={placeholder}
          variant="bordered"
          className={`cursor-text !text-gray-800 [&_input]:!text-gray-800 [&_input:-webkit-autofill]:![-webkit-text-fill-color:#1f2937] [&_input:-webkit-autofill:hover]:![-webkit-text-fill-color:#1f2937] [&_input:-webkit-autofill:focus]:![-webkit-text-fill-color:#1f2937] [&_input:-webkit-autofill:active]:![-webkit-text-fill-color:#1f2937] ${className || ''}`}
          style={{
            color: '#1f2937',
          }}
          classNames={mergedClassNames}
          isInvalid={!!error}
          {...props}
        />
        {/* Reserved space for error message */}
        <div className="h-5">{error && <p className="text-red-500 text-sm">{error}</p>}</div>
      </div>
    );
  },
);

CustomInput.displayName = 'CustomInput';
